/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import {
  ColDef,
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  Module,
  ProcessCellForExportParams,
  SelectionChangedEvent,
} from '@ag-grid-community/core'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { SetFilter } from '@ag-grid-enterprise/set-filter'
import { HttpErrorResponse } from '@angular/common/http'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl } from '@angular/forms'
import {
  HeliosColumnTypes,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
  generateHeliosAgGridSidebarFromScratch,
  generateMdiIcon,
  heliosDefaultColWidth,
  heliosDefaultToolPanels,
  heliosMultiRowSelectionConfig,
} from '@helios/ag-grid-config'
import { FilterModalService } from '@helios/filter-modal'
import {
  AccountRowUpdateEvent,
  EventType,
  GlobalMessagingService,
  HeliosMessageType,
} from '@helios/messaging'
import { countFilters } from '@helios/server-side-util'
import {
  JobsPayload,
  RootState,
  selectSelectedReconDate,
} from '@helios/state-management'
import {
  DensityLevel,
  FilterGroupsToolPanelParams,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
  WorkspaceUpdateEventObject,
  createFilterGroupsToolPanel,
} from '@helios/user-preferences'
import { fromStringListToNumberArray, isNullOrEmpty } from '@helios/utils'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfColumnTypes, NfTooltipParams } from '@northfork/ag-grid'
import { AgGridState } from '@northfork/ag-grid-angular'
import {
  ISODateTimeFormat,
  composeFieldPath,
  formatDate,
} from '@northfork/basics'
import { Daterange } from '@northfork/ng-basics'
import format from 'date-fns/format'
import sub from 'date-fns/sub'
import { createPanelSettingsToolPanel } from 'libs/generic-components/src/lib/panel-settings-tool-panel/utils/panel-settings-tool-panel-sidebar-utils'
import { Subject, of } from 'rxjs'
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeAll,
  takeUntil,
  tap,
  toArray,
} from 'rxjs/operators'
import { JobsKey } from '../../../panel-key'
import { durationCellRenderer } from '../../grid-helpers/job-duration-cell-renderer'
import { jobStatusCellRenderer } from '../../grid-helpers/jobs-status-cell-renderer'
import { Job, JobActionResponseMessage } from '../../model/job.model'
import { JobsService } from '../../services/jobs.service'
import { ActionErrorModalComponent } from '../action-error-modal/action-error-modal.component'

const JobPath = composeFieldPath([] as Job[])
const JobsMainGridKey = `${JobsKey}-MainGrid`
const DEFAULT_BEGIN_DATE = format(sub(new Date(), { days: 3 }), 'yyyy-MM-dd')
const DEFAULT_END_DATE = format(new Date(), 'yyyy-MM-dd')
const JobGridKey = `${JobsKey}-JobGrid`

@Component({
  selector: 'hs-jobs-grid',
  templateUrl: './jobs-grid.component.html',
  styleUrls: ['./jobs-grid.component.scss'],
  providers: [FilterModalService],
})
export class JobsGridComponent implements OnInit, OnDestroy {
  filterCount = 0
  private readonly _destroyed$ = new Subject<void>()
  data: Job[] | null = []
  gridApi?: AgGridApi
  invalidPermission = false
  clearFiltersOnAccountLoadToggle = new FormControl(true)
  readonly gridOptions: GridOptions = this.createGridOptions()
  readonly gridModules: Module[] = [RangeSelectionModule]

  @Input('selectedJobs') selectedJobs: JobsPayload | undefined
  savedFilterState: { [columnName: string]: unknown } | undefined
  @Input() set focusedIds(accountIds: number[] | null) {
    if ((accountIds?.length ?? -1) > 0) {
      this.accountIdsChanged(accountIds)
    }
  }
  @Input() set selectedCommandsJobIds(jobIds: string | undefined) {
    if (this._selectedCommandsJobIds != jobIds) {
      this._selectedCommandsJobIds = jobIds
      this.searchJobIdsForm.setValue(jobIds)
    }
  }
  @Output() selectJobIds = new EventEmitter<number[]>()
  @Output() resetSelectedJob = new EventEmitter()
  @Output() jobIdSearchChanged = new EventEmitter<''>()

  private searchAccountIds: number[] = []
  searchAccountIdsForm = new FormControl('')
  dateType = new FormControl('CREATED')
  searchJobIdsForm = new FormControl('')
  selectedReconDate?: Date
  _selectedCommandsJobIds?: string

  isLoading = false

  dateRange: Daterange = {
    beginDate: DEFAULT_BEGIN_DATE,
    endDate: DEFAULT_END_DATE,
  }

  get filterGroupsToolPanelParams(): FilterGroupsToolPanelParams<unknown> {
    return {
      gridKey: JobsMainGridKey,
      getExternalFilters: () => ({}),
      setExternalFilters: () => undefined,
      cacheLoaded: of(true),
    }
  }

  clearFiltersEnabled = false

  constructor(
    private readonly jobsService: JobsService,
    public filterModalService: FilterModalService,
    private readonly preferences: UserPreferencesService,
    private readonly messagingService: GlobalMessagingService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly modalService: NgbModal,
    private readonly store: Store<RootState>,
  ) {}

  ngOnInit() {
    this.messagingService
      .on<unknown, AccountRowUpdateEvent<Record<string, string>>>(
        EventType.MESSAGE,
        HeliosMessageType.AccountUpdate,
      )
      .pipe(
        takeUntil(this._destroyed$),
        filter(
          sse =>
            this.searchAccountIds.includes(sse.payload.accountId) &&
            sse.payload.updatedFields.includes('perseusRunStatus'),
        ),
        debounceTime(100),
        tap(() => this.updateData()),
      )
      .subscribe()

    this.searchJobIdsForm.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(() => {
        this.updateQuickSearchJobIds()
      })
    this.store
      .select(selectSelectedReconDate)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((selectedReconDate?: Date) => {
        this.selectedReconDate = selectedReconDate
        this.setDateRange(this.dateType.value, false)
      })

    this.dateType.valueChanges.subscribe(dateType => {
      this.setDateRange(dateType, true)
    })
    this.clearFiltersOnAccountLoadToggle.valueChanges.subscribe(() => {
      this.updateGridConfig()
    })
  }

  setDateRange(dateType: string, loadData?: boolean) {
    if (dateType === 'RECON') {
      const reconDate = new Date(
        this.selectedReconDate ? this.selectedReconDate + 'T00:00:00' : '',
      )
      this.dateRange = {
        endDate: format(reconDate, 'yyy-MM-dd'),
        beginDate: format(sub(reconDate, { days: 3 }), 'yyyy-MM-dd'),
      }
      this.loadData()
      this.updateGridConfig()
    } else {
      this.dateRange = {
        endDate: DEFAULT_END_DATE,
        beginDate: DEFAULT_BEGIN_DATE,
      }
      if (loadData) {
        this.loadData()
        this.updateGridConfig()
      }
    }
  }

  ngOnDestroy() {
    this.resetSelectedJob.emit()
    this._destroyed$.next()
    this._destroyed$.complete()
    this.gridApi?.api?.destroy()
  }

  accountIdsChanged(values: number[] | null) {
    if (this.clearFiltersOnAccountLoadToggle.value) {
      this.searchJobIdsForm.reset()
    }
    this.searchAccountIds = values ?? []
    this.searchAccountIdsForm.setValue(this.searchAccountIds.join(', '))
    this.onAccountFilterChange()
  }

  async updateData() {
    if (!this.searchAccountIdsForm.value) {
      return
    }
    this.invalidPermission = false
    this.data = await this.getJobsForSearchAccountIds()
  }

  updateGridConfig() {
    const panelSettingsChangedEvent = new CustomEvent('panelSettingsChanged', {
      detail: {
        api: this.gridApi,
        panelSettings: this.getCurrentPanelSettingsObject(),
      } as WorkspaceUpdateEventObject,
    })
    this.gridApi?.api.dispatchEvent(panelSettingsChangedEvent)
  }

  getCurrentPanelSettingsObject() {
    const panelSettings: { [key: string]: unknown } = {}
    panelSettings['clearFiltersOnAccountLoad'] =
      this.clearFiltersOnAccountLoadToggle.value
    panelSettings['dateType'] = this.dateType.value
    panelSettings['dateRange'] = this.dateRange
    return panelSettings
  }

  private applyGridConfig(agGridStateSaved: AgGridState) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const panelSettings = agGridStateSaved.panelSettings as any

    if (panelSettings) {
      if ('clearFiltersOnAccountLoad' in panelSettings) {
        this.clearFiltersOnAccountLoadToggle.patchValue(
          Boolean(panelSettings.clearFiltersOnAccountLoad),
          { emitEvent: false },
        )
      }

      if (!panelSettings.clearFiltersOnAccountLoad) {
        if ('dateType' in panelSettings) {
          this.dateType.patchValue(String(panelSettings.dateType), {
            emitEvent: false,
          })
        }
        if ('dateRange' in panelSettings) {
          this.onDateRangeChange(panelSettings.dateRange, false)
        }
      }
    }
  }

  async loadData() {
    if (!this.searchAccountIdsForm.value) {
      return
    }

    this.isLoading = true
    this.invalidPermission = false
    this.data = await this.getJobsForSearchAccountIds()
    this.isLoading = false
  }

  /** @internal **/
  getJobsForSearchAccountIds() {
    if (this.selectedJobs?.reconDate) {
      this.dateRange = {
        ...this.dateRange,
        beginDate: this.selectedJobs.reconDate.slice(0, 10),
      }
    }
    return this.jobsService
      .getJobs({
        endDate: this.dateRange.endDate ?? DEFAULT_END_DATE,
        startDate: this.dateRange.beginDate ?? DEFAULT_BEGIN_DATE,
        accountIds: this.formatAccounts(this.searchAccountIdsForm.value),
        dateType: this.dateType.value,
      })
      .pipe(
        mergeAll(),
        map(
          job =>
            ({
              ...job,
              lastUpdated: new Date(job.lastUpdated),
            } as Job),
        ),
        toArray(),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 403) {
            this.invalidPermission = true
            this.isLoading = false
          }
          return []
        }),
      )
      .toPromise()
  }

  async cancelJobs(jobIds: number[]) {
    const response = await this.jobsService.cancelJobs(jobIds).toPromise()
    if (response.allSuccessful) {
      this.updateData()
    }
    this.launchErrorModal(
      response.allSuccessful,
      'cancel',
      response.jobIdsUnableToCancel,
    )
  }

  async resubmitJobs(jobIds: number[]) {
    const response = await this.jobsService.resubmitJobs(jobIds).toPromise()
    if (response.allSuccessful) {
      this.updateData()
    }
    this.launchErrorModal(
      response.allSuccessful,
      'resubmit',
      response.jobIdsUnableToResubmit,
    )
  }

  launchErrorModal(
    isSuccessful: boolean,
    type: 'cancel' | 'resubmit',
    messageMap: JobActionResponseMessage,
  ) {
    if (isSuccessful) return

    const modalRef = this.modalService.open(ActionErrorModalComponent, {
      centered: true,
      keyboard: false,
    })

    modalRef.componentInstance.type = type
    modalRef.componentInstance.messageMap = messageMap
  }

  formatAccounts(accountIds: string): number[] {
    return accountIds.split(',').map(x => Number(x.trim()))
  }

  private createGridOptions(): GridOptions {
    return {
      defaultColDef: {
        filterParams: {
          closeOnApply: true,
          defaultToNothingSelected: true,
        },
        width: heliosDefaultColWidth.Default,
        sortable: true,
      },
      enableBrowserTooltips: true,
      columnDefs: this.createBaseColumnDefs(),
      onFirstDataRendered: () => this.applyFiltersAfterFirstRender(),
      onGridReady: event => this.onGridReady(event),
      getContextMenuItems: params => this.getContextMenuItems(params),
      icons: {
        panelSettings: generateMdiIcon('mdi-cog'),
      },
      sideBar: generateHeliosAgGridSidebarFromScratch([
        createPanelSettingsToolPanel({
          controls: [
            {
              label: 'Clear Filters on Load',
              formControl: this.clearFiltersOnAccountLoadToggle,
            },
          ],
        }),
        ...heliosDefaultToolPanels,
        createFilterGroupsToolPanel<unknown>(this.filterGroupsToolPanelParams),
      ]),
      immutableData: true,
      onFilterChanged: params => this.onFilterChanged(params),
      getRowNodeId: event => `${event.jobId}`,
      onSelectionChanged: event => this.onRowSelected(event),
      ...heliosMultiRowSelectionConfig,
      processCellForClipboard: (params: ProcessCellForExportParams) => {
        if (params.column.getColDef().type === 'DateTime' && params.value) {
          return formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat)
        }
        return params.value
      },
    }
  }
  applyFiltersAfterFirstRender() {
    // apply filter model when it has any values in it
    if (Object.keys(this.savedFilterState ?? {}).length) {
      const colFilter = this.gridApi?.api.getFilterInstance(
        'userName',
      ) as SetFilter<string>
      colFilter.refreshFilterValues()
      if (this.savedFilterState) {
        this.gridApi?.api.setFilterModel(this.savedFilterState)
      }
    }
  }

  private onJobIdFilterChanged(params: FilterChangedEvent) {
    const model = params.api.getFilterInstance('jobId')?.getModel()
    const isFilteredByJobs = model?.type === 'in' && model?.filter.length > 0
    model?.filterType === 'number'

    if (isFilteredByJobs) {
      this.searchJobIdsForm.patchValue(model?.filter, { emitEvent: false })
    } else {
      this.searchJobIdsForm.reset('', { emitEvent: false })
    }
    if (!this.gridApi) {
      return
    }
    const filterModels = this.gridApi?.api.getFilterModel() ?? {}
    const activeFilters = Object.keys(filterModels)
    this.clearFiltersEnabled = activeFilters.length > 0
    this.filterCount = countFilters(this.gridApi?.api) ?? 0
  }

  onRowSelected(params: SelectionChangedEvent) {
    const jobIds = this.getSelectedJobs(params.api)
    this.selectJobIds.emit(jobIds.map(x => x.jobId))
  }

  /** @internal */
  getContextMenuItems(
    params: GetContextMenuItemsParams,
  ): (string | MenuItemDef)[] {
    const selectedJobs = this.getSelectedJobs(params.api)

    const items: (string | MenuItemDef)[] = [
      {
        name: `Cancel Job${selectedJobs.length > 1 ? 's' : ''}`,
        action: () => this.cancelJobs(selectedJobs.map(x => x.jobId)),
        disabled: !selectedJobs.every(x =>
          x.availableActions.includes('CANCEL'),
        ),
      },
      {
        name: `Resubmit Job${selectedJobs.length > 1 ? 's' : ''}`,
        action: () => this.resubmitJobs(selectedJobs.map(x => x.jobId)),
        disabled: !selectedJobs.every(x =>
          x.availableActions.includes('RESUBMIT'),
        ),
      },
    ]

    return items
  }

  /** @internal */
  getSelectedJobs(api: GridApi): Job[] {
    return api.getSelectedNodes().map(x => (x.data as Job) ?? {})
  }

  private createBaseColumnDefs(): ColDef[] {
    return [
      {
        field: JobPath('status'),
        type: NfColumnTypes.Identifier,
        cellRenderer: jobStatusCellRenderer,
        width: heliosDefaultColWidth.SmallIcon,
        tooltipValueGetter: params => params.value,
        tooltipComponentParams: {
          showTooltip: () => true,
        } as NfTooltipParams,
      },
      {
        field: JobPath('accountId'),
        headerName: 'Account ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: JobPath('jobId'),
        headerName: 'Job ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: JobPath('reconDate'),
        type: NfColumnTypes.Date,
        width: heliosDefaultColWidth.DateCol,
      },
      {
        field: JobPath('userName'),
        filter: true,
      },
      {
        field: JobPath('userId'),
        headerName: 'User ID',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
        hide: true,
      },
      {
        field: JobPath('numCommands'),
        headerName: 'Commands',
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.MediumIcon,
      },
      {
        field: JobPath('lastUpdated'),
        type: HeliosColumnTypes.DateTime,
        valueFormatter: params =>
          formatDate(params.value, 'MM/dd/yyyy HH:mm', ISODateTimeFormat) ??
          params.value,
        width: heliosDefaultColWidth.DateTime,
        sortIndex: 0,
        sort: 'asc',
      },
      {
        field: JobPath('notes'),
      },
      { field: JobPath('fatalErrors'), type: NfColumnTypes.Identifier },
      { field: JobPath('nonFatalErrors'), type: NfColumnTypes.Identifier },
      {
        field: JobPath('runEtls'),
        type: HeliosColumnTypes.Boolean,
        width: heliosDefaultColWidth.MediumIcon,
      },
      {
        field: JobPath('isCustodyRun'),
        headerName: 'Trading Run',
        valueGetter: params => !params.data.isCustodyRun,
        type: HeliosColumnTypes.Boolean,
        width: heliosDefaultColWidth.MediumIcon,
      },
      {
        field: JobPath('priority'),
        type: NfColumnTypes.Identifier,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: JobPath('weight'),
        width: heliosDefaultColWidth.Id,
      },
      { field: JobPath('duration'), cellRenderer: durationCellRenderer },
    ]
  }

  searchByAccountIds() {
    this.loadData()
  }

  onDateRangeChange = (range: Daterange, saveSettings: boolean) => {
    this.dateRange = range
    this.loadData()
    if (saveSettings) {
      this.updateGridConfig()
    }
  }

  updateAccountsFilter(selectedAccounts: string[]) {
    const formValue = this.searchAccountIdsForm.value as string
    const accountsInSearch = formValue
      .split(',')
      .map(x => x.trim())
      .filter(y => y !== '')
    const newValues = selectedAccounts.filter(
      id => !accountsInSearch.includes(id),
    )
    if (newValues.length > 0) {
      const newAccounts = accountsInSearch.concat(newValues)
      this.searchAccountIdsForm.setValue(newAccounts.join(','))
    }
  }

  onAccountFilterChange() {
    this.loadData()
  }

  async updateQuickSearchJobIds() {
    if (this.searchJobIdsForm.invalid) return
    const jobIdsStrings = this.searchJobIdsForm.value
    const jobIds = fromStringListToNumberArray(jobIdsStrings)
      .map(x => -Math.abs(x))
      .join(', ')
    const jobIdFilter = this.gridApi?.api.getFilterInstance('jobId')

    const isJobIdNullOrEmpty = isNullOrEmpty(jobIdsStrings)
    if (!isJobIdNullOrEmpty) {
      jobIdFilter?.setModel({
        type: 'in',
        filterType: 'number',
        filter: jobIds,
      })
    } else jobIdFilter?.setModel(null)
    //applied timeout so that the onFilterChanged is called once the filter is correctly applied on the column
    await new Promise(f => setTimeout(f, 40))
    this.gridApi?.api.onFilterChanged()

    this.jobIdSearchChanged.emit(this.searchJobIdsForm.value)
  }

  onFilterChanged(params: FilterChangedEvent) {
    const filtersPresent = this.gridApi?.api.isAnyFilterPresent() ?? false
    this.clearFiltersEnabled = filtersPresent
    this.onJobIdFilterChanged(params)
  }

  launchFiltersModal() {
    this.filterModalService.launchFiltersModal()
  }

  clearFilters() {
    this.searchJobIdsForm.reset()
    this.gridApi?.api.setFilterModel(null)
  }

  /**@Internal */
  async onGridReady(params: GridReadyEvent): Promise<void> {
    this.gridApi = params

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this._destroyed$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(
          params.api,
          DensityLevel.Compact === level,
        ),
      )

    enableHeliosMultiRowDragSelection(this.gridApi.api)
    enableHeliosCopyBehavior(this.gridApi.api)
    this._registerGridDataChangeEvent(this.gridApi.api)

    /* why move this later ? so that all filter change events are called before workspace is rendered and only if workspace 
    has filter model it ever again calls filter model change not otherwise avoiding unnecessary race conditions */
    const agGridStateSaved = await this.savedViewService.manageSavedView(
      params,
      JobGridKey,
      this._destroyed$,
      true,
      this.getCurrentPanelSettingsObject(),
    )
    this.savedFilterState = agGridStateSaved.filterState
    this.applyGridConfig(agGridStateSaved)
  }

  private _registerGridDataChangeEvent(gridApi: GridApi) {
    gridApi.addEventListener('modelUpdated', () => {
      if (this.selectedJobs) {
        const { jobIds } = this.selectedJobs
        // Step 1: Select job in grid
        this.searchJobIdsForm.setValue(`${jobIds}`) // will uncomment after discussion with Jakub
        this.selectedJobs = undefined
        // Step 2: Fetch command for selected job node
        this.selectJobIds.emit(jobIds)
      } else if (this.searchJobIdsForm.value) {
        /* Although at first glance, below code won't make sense 
          but its a hack to reapply preserved jobId filter again in grid
          Actually, somehow `searchJobIdsForm` text box preserves searched jobId 
        */
        this.searchJobIdsForm.setValue(this.searchJobIdsForm.value)
      }
    })
  }
}
